<template>
  <v-container class="hyp-map-controls pa-3 ma-0">
    <!-- Points Control -->
    <div class="mb-4">
      <h3 class="pull-left">Points</h3>
      <v-switch
        class="pull-right mt-0"
        v-model="mapPointsControl"
        @change="showHideLayers('points', mapPointsControl)"
        hide-details
      ></v-switch>
      <div class="clearfix"></div>
    </div>

    <v-expansion-panels accordion flat elevation-0>
      <v-expansion-panel v-for="section in pointsSections" :key="section.id">
        <v-expansion-panel-header hide-actions class="pa-2">
          <template v-slot:default="{ open }">
            <div>
              <v-icon>
                <template v-if="open">mdi-chevron-up</template>
                <template v-else>mdi-chevron-down</template>
              </v-icon>
              <h4 class="hyp-map-controls-section-title pl-2">
                {{ section.title }}
              </h4>
              <v-checkbox
                class="ma-0 pa-0 pull-right"
                v-model="section.visible"
                hide-details
                @change="handleChangePoints(section)"
              ></v-checkbox>
            </div>
          </template>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <span v-if="!section.contentComp">
            Lorem ipsum dolor sit amet. Section details.</span
          >
          <component
            v-if="section.contentComp"
            v-bind:is="section.contentComp"
          ></component>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Overlays Control -->
    <div class="mt-10 mb-4">
      <h3 class="pull-left">Overlays</h3>
      <v-switch
        class="pull-right mt-0"
        v-model="mapOverlaysControl"
        @change="showHideLayers('overlays', mapOverlaysControl)"
        hide-details
      ></v-switch>
      <div class="clearfix"></div>
    </div>

    <StabilityOpacityBar class="mx-2"></StabilityOpacityBar>
    <v-radio-group
      hide-details
      class="pa-0 ma-0"
      v-model="mapOverlaysControlType"
      @change="handleChangeOverlays"
    >
      <v-expansion-panels accordion flat elevation-0>
        <v-expansion-panel
          v-for="section in overlaysSections"
          :key="section.id"
        >
          <v-expansion-panel-header hide-actions class="pa-2">
            <template v-slot:default="{ open }">
              <div>
                <v-icon>
                  <template v-if="open">mdi-chevron-up</template>
                  <template v-else>mdi-chevron-down</template>
                </v-icon>
                <h4 class="hyp-map-controls-section-title pl-2">
                  {{ section.title }}
                </h4>
                <v-radio
                  class="ma-0 pa-0 pull-right"
                  hide-details
                  :value="section"
                >
                </v-radio>
              </div>
            </template>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <span v-if="!section.contentComp">
              Lorem ipsum dolor sit amet. Section details.</span
            >
            <component
              v-if="section.contentComp"
              v-bind:is="section.contentComp"
            ></component>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-radio-group>

    <!-- Base Layer Control -->
    <div class="mt-10 mb-4">
      <h3>Base Layer</h3>
    </div>
    <BaseLayer :map-id="mapId" :options="baseLayerControlOptions"></BaseLayer>
  </v-container>
</template>

<script>
import lodashCloneDeep from "lodash/cloneDeep";

import { mapGetters } from "vuex";
import { MODULE_PREFIX_MAP, MODULE_PREFIX_USER } from "@/store";
import { SAVE_PREFERENCES, GET_MAP_REFRESH_DATA } from "@/store/actions.type";

import {
  POINTS_SECTIONS,
  OVERLAYS_SECTIONS,
} from "@/common/constants/mapControls";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import { showHideLayer } from "@/common/maps/layers";

import { populateDefaultPreferences as pulseDefaultPreferences } from "@/services/grids/stability.service";
import { populateDefaultPreferences as riskRatingDefaultPreferences } from "@/services/controlRisks/risks.service";

export default {
  name: "MapControls",
  components: {
    StabilityOpacityBar: () =>
      import("@/components/grids/stability/StabilityOpacityBar"),
    BaseLayer: () => import("@/components/map/BaseLayer"),
  },
  data: () => ({
    mapPointsControl: true,
    mapOverlaysControl: true,
    overlaysSections: OVERLAYS_SECTIONS,
    pointsSections: POINTS_SECTIONS,
    mapOverlaysControlType: null,
  }),
  computed: {
    ...mapGetters({
      maps: MODULE_PREFIX_MAP + "maps",
      preferences: MODULE_PREFIX_USER + "preferences",
    }),
    mapId() {
      return MAIN_MAP_ID;
    },
    map() {
      return this.maps[MAIN_MAP_ID];
    },
    baseLayerControlOptions() {
      return {
        showInline: true,
        position: "relative",
        btnSize: 36,
        bottom: null,
        right: null,
      };
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      this.mapPointsControl =
        this.preferences?.mapControls?.mapPointsControl ?? true;
      this.mapOverlaysControl =
        this.preferences?.mapControls?.mapOverlaysControl ?? true;

      this.pointsSections = this.pointsSections.map((section) => {
        if (section.id === "assets") {
          section.visible = this.preferences?.assets?.enabled ?? false;
        }
        if (section.id === "my_incidents") {
          section.visible = this.preferences?.myIncidents?.enabled ?? false;
        }
        if (section.id === "hotspots") {
          section.visible = this.preferences?.grids?.hotspots?.enabled ?? false;
        }
        if (section.id === "searches") {
          section.visible = true; // Should change when work on searches section ticket
        }
        if (section.id === "places") {
          section.visible = true; // Should change when/if work on places section ticket
        }
        return section;
      });

      this.mapOverlaysControlType = this.preferences?.powerOutage?.enabled
        ? this.overlaysSections.find((s) => s.id === "power_outage")
        : this.preferences?.grids?.stability?.enabled
        ? this.overlaysSections.find((s) => s.id === "pulse")
        : this.preferences?.controlRisks?.riskRatings?.mapEnabled
        ? this.overlaysSections.find((s) => s.id === "risk_ratings")
        : null;
    },
    defaultMapControlsPref() {
      return {
        mapPointsControl: true,
        mapOverlaysControl: true,
      };
    },
    showHideLayers(type, visible) {
      const selectedLayers = this.map
        .getStyle()
        .layers.filter((layer) => {
          return (
            typeof layer?.metadata === "string" &&
            layer.metadata.indexOf(`seerist-${type}-`) >= 0
          );
        })
        ?.map((layer) => layer.id);
      showHideLayer(this.map, selectedLayers, visible);

      let preferences = lodashCloneDeep(this.preferences);
      preferences.mapControls =
        preferences.mapControls || this.defaultMapControlsPref();
      preferences.mapControls.mapPointsControl = this.mapPointsControl;
      preferences.mapControls.mapOverlaysControl = this.mapOverlaysControl;
      this.$store.dispatch(MODULE_PREFIX_USER + SAVE_PREFERENCES, preferences);
    },
    async handleChangePoints(section) {
      let preferences = lodashCloneDeep(this.preferences);
      preferences = pulseDefaultPreferences(preferences);
      preferences.assets = preferences.assets || {};
      preferences.myIncidents = preferences.myIncidents || {};

      if (section.id === "assets") {
        preferences.assets.enabled = section.visible;
      }
      if (section.id === "my_incidents") {
        preferences.myIncidents.enabled = section.visible;
      }
      if (section.id === "hotspots") {
        preferences.grids.hotspots.enabled = section.visible;
      }
      if (section.id === "searches") {
        // Should change when work on searches section ticket
      }
      if (section.id === "places") {
        // Should change when/if work on places section ticket
      }
      await this.$store.dispatch(
        MODULE_PREFIX_USER + SAVE_PREFERENCES,
        preferences
      );
      // Reload map based on pref
      this.$store.dispatch(MODULE_PREFIX_MAP + GET_MAP_REFRESH_DATA, true);
      preferences = null;
    },
    async handleChangeOverlays() {
      let preferences = lodashCloneDeep(this.preferences);
      preferences = riskRatingDefaultPreferences(preferences);
      preferences = pulseDefaultPreferences(preferences);
      preferences.powerOutage = preferences.powerOutage || {};
      preferences.controlRisks.riskRatings.mapEnabled = false;
      preferences.grids.stability.enabled = false;
      preferences.powerOutage.enabled = false;
      if (this.mapOverlaysControlType.id === "risk_ratings") {
        preferences.controlRisks.riskRatings.mapEnabled = true;
      } else if (this.mapOverlaysControlType.id === "pulse") {
        preferences.grids.stability.enabled = true;
      } else if (this.mapOverlaysControlType.id === "power_outage") {
        preferences.powerOutage.enabled = true;
      }
      await this.$store.dispatch(
        MODULE_PREFIX_USER + SAVE_PREFERENCES,
        preferences
      );
      // Reload map based on pref
      this.$store.dispatch(MODULE_PREFIX_MAP + GET_MAP_REFRESH_DATA, true);
      preferences = null;
    },
  },
};
</script>

<style scoped>
.hyp-map-controls {
  width: 350px;
  max-height: calc(100vh - 100px) !important;
  overflow-y: auto;
}
.hyp-map-controls .v-expansion-panel {
  border-bottom: 1px solid #dddddd;
}
.hyp-map-controls .v-expansion-panels {
  border-top: 1px solid #dddddd;
}
.hyp-map-controls-section-title {
  display: inline-block;
}
</style>
